import { Card } from 'pokemon-tcg-sdk-typescript';
import { PokemonCard, PokemonSet } from '../types';
import { supabase } from '../lib/supabase';
import { valuableCards } from '../data/valuableCards';
import { log } from '../components/DebugLogger';

// Memory cache with improved structure
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  stale: boolean;
}

const memoryCache = new Map<string, CacheEntry<any>>();
const STALE_WHILE_REVALIDATE_DURATION = 2 * 60 * 1000; // 2 minutes
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Background revalidation queue
const revalidationQueue = new Set<string>();
let isProcessingQueue = false;
let lastFocusTime = Date.now();

// Add tab focus listener
if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => {
    const now = Date.now();
    // If more than 1 minute has passed since last focus, invalidate cache
    if (now - lastFocusTime > 60 * 1000) {
      log('Tab focused after inactivity, clearing cache');
      memoryCache.clear();
    }
    lastFocusTime = now;
  });
}

// Cache management functions
function getCacheKey(type: string, id: string): string {
  return `${type}:${id}`;
}

function getFromCache<T>(type: string, id: string): T | null {
  const key = getCacheKey(type, id);
  const entry = memoryCache.get(key);
  const now = Date.now();
  
  if (!entry) {
    log(`Cache miss for ${key}`);
    return null;
  }
  
  const age = now - entry.timestamp;
  
  // If cache is fresh, return immediately
  if (age < STALE_WHILE_REVALIDATE_DURATION) {
    log(`Cache hit (fresh) for ${key}`);
    return entry.data;
  }
  
  // If cache is stale, queue revalidation
  if (age < CACHE_DURATION && !entry.stale) {
    if (!entry.stale) {
      entry.stale = true;
      log(`Cache hit (stale) for ${key}, queueing revalidation`);
      queueRevalidation(type, id);
      return entry.data;
    }
  }
  
  // Cache is expired
  log(`Cache expired for ${key}, removing entry`);
  memoryCache.delete(key);
  return null;
}

function setInCache<T>(type: string, id: string, data: T): void {
  const key = getCacheKey(type, id);
  memoryCache.set(key, {
    data,
    timestamp: Date.now(),
    stale: false
  });
}

function queueRevalidation(type: string, id: string): void {
  revalidationQueue.add(getCacheKey(type, id));
  if (!isProcessingQueue) {
    processRevalidationQueue();
  }
}

async function processRevalidationQueue(): Promise<void> {
  if (isProcessingQueue || revalidationQueue.size === 0) return;
  
  isProcessingQueue = true;
  
  try {
    const promises: Promise<void>[] = [];
    
    for (const key of revalidationQueue) {
      const [type, id] = key.split(':');
      
      if (type === 'set') {
        promises.push(
          getSetCards(id, true)
            .then(() => revalidationQueue.delete(key))
            .catch(console.error)
        );
      }
      
      // Add delay between requests
      if (promises.length >= 3) {
        await Promise.all(promises);
        promises.length = 0;
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    await Promise.all(promises);
  } finally {
    isProcessingQueue = false;
    
    // If new items were added during processing, run again
    if (revalidationQueue.size > 0) {
      setTimeout(processRevalidationQueue, 100);
    }
  }
}

// Set name normalization mapping
const SET_NAME_MAPPING: Record<string, string> = {
  // Base Set Series
  'base': 'base1',
  'base set': 'base1',
  'jungle': 'base2',
  'fossil': 'base3',
  'base set 2': 'base4',
  'team rocket': 'base5',
  'gym heroes': 'gym1',
  'gym challenge': 'gym2',

  // Neo Series
  'neo genesis': 'neo1',
  'neo discovery': 'neo2',
  'neo revelation': 'neo3',
  'neo destiny': 'neo4',

  // Legendary Collection
  'legendary collection': 'base6',

  // e-Card Series
  'expedition base set': 'ecard1',
  'aquapolis': 'ecard2',
  'skyridge': 'ecard3',

  // EX Series
  'ex ruby sapphire': 'ex1',
  'ex ruby & sapphire': 'ex1',
  'ex ruby and sapphire': 'ex1',
  'ruby & sapphire': 'ex1',
  'ruby and sapphire': 'ex1',
  'ex sandstorm': 'ex2',
  'sandstorm': 'ex2',
  'dragon': 'ex3',
  'ex dragon': 'ex3',
  'dragon ex': 'ex3',
  'ex series dragon': 'ex3',
  'ex team magma vs team aqua': 'ex4',
  'team magma vs team aqua': 'ex4',
  'ex hidden legends': 'ex5',
  'hidden legends': 'ex5',
  'ex firered leafgreen': 'ex6',
  'ex firered & leafgreen': 'ex6',
  'ex firered and leafgreen': 'ex6',
  'firered & leafgreen': 'ex6',
  'firered and leafgreen': 'ex6',
  'ex team rocket returns': 'ex7',
  'team rocket returns': 'ex7',
  'ex deoxys': 'ex8',
  'deoxys': 'ex8',
  'ex emerald': 'ex9',
  'emerald': 'ex9',
  'ex unseen forces': 'ex10',
  'unseen forces': 'ex10',
  'ex delta species': 'ex11',
  'delta species': 'ex11',
  'ex legend maker': 'ex12',
  'legend maker': 'ex12',
  'ex holon phantoms': 'ex13',
  'holon phantoms': 'ex13',
  'ex crystal guardians': 'ex14',
  'crystal guardians': 'ex14',
  'ex dragon frontiers': 'ex15',
  'dragon frontiers': 'ex15',
  'ex power keepers': 'ex16',
  'power keepers': 'ex16',

  // POP Series
  'pop series 1': 'pop1',
  'pop series 2': 'pop2',
  'pop series 3': 'pop3',
  'pop series 4': 'pop4',
  'pop series 5': 'pop5',
  'pop series 6': 'pop6',
  'pop series 7': 'pop7',
  'pop series 8': 'pop8',
  'pop series 9': 'pop9',
  'pop 1': 'pop1',
  'pop 2': 'pop2',
  'pop 3': 'pop3',
  'pop 4': 'pop4',
  'pop 5': 'pop5',
  'pop 6': 'pop6',
  'pop 7': 'pop7',
  'pop 8': 'pop8',
  'pop 9': 'pop9',
  'pop': null,

  // Diamond & Pearl Series
  'diamond pearl': 'dp1',
  'diamond & pearl': 'dp1',
  'diamond and pearl': 'dp1',
  'mysterious treasures': 'dp2',
  'secret wonders': 'dp3',
  'great encounters': 'dp4',
  'majestic dawn': 'dp5',
  'legends awakened': 'dp6',
  'stormfront': 'dp7',

  // Platinum Series
  'platinum': 'pl1',
  'rising rivals': 'pl2',
  'supreme victors': 'pl3',
  'arceus': 'pl4',

  // HeartGold & SoulSilver Series
  'heartgold soulsilver': 'hgss1',
  'heartgold & soulsilver': 'hgss1',
  'heartgold and soulsilver': 'hgss1',
  'hs triumphant': 'hgss4',
  'hs—triumphant': 'hgss4',
  'hstriumphant': 'hgss4',
  'triumphant': 'hgss4',
  'hs undaunted': 'hgss3',
  'hs—undaunted': 'hgss3',
  'hsundaunted': 'hgss3',
  'undaunted': 'hgss3',
  'hs unleashed': 'hgss2',
  'hs—unleashed': 'hgss2',
  'hsunleashed': 'hgss2',
  'unleashed': 'hgss2',
  'call of legends': 'col1',

  // Black & White Series
  'black white': 'bw1',
  'black & white': 'bw1',
  'black and white': 'bw1',
  'emerging powers': 'bw2',
  'noble victories': 'bw3',
  'next destinies': 'bw4',
  'dark explorers': 'bw5',
  'dragons exalted': 'bw6',
  'dragon vault': 'dv1',
  'boundaries crossed': 'bw7',
  'plasma storm': 'bw8',
  'plasma freeze': 'bw9',
  'plasma blast': 'bw10',
  'legendary treasures': 'bw11',

  // XY Series
  'kalos starter set': 'xy0',
  'xy': 'xy1',
  'flashfire': 'xy2',
  'furious fists': 'xy3',
  'phantom forces': 'xy4',
  'primal clash': 'xy5',
  'double crisis': 'dc1',
  'roaring skies': 'xy6',
  'ancient origins': 'xy7',
  'breakthrough': 'xy8',
  'breakpoint': 'xy9',
  'generations': 'g1',
  'fates collide': 'xy10',
  'steam siege': 'xy11',
  'evolutions': 'xy12',

  // Sun & Moon Series
  'sun moon': 'sm1',
  'sun & moon': 'sm1',
  'sun and moon': 'sm1',
  'guardians rising': 'sm2',
  'burning shadows': 'sm3',
  'crimson invasion': 'sm4',
  'ultra prism': 'sm5',
  'forbidden light': 'sm6',
  'celestial storm': 'sm7',
  'dragon majesty': 'sm75',
  'lost thunder': 'sm8',
  'team up': 'sm9',
  'unbroken bonds': 'sm10',
  'unified minds': 'sm11',
  'cosmic eclipse': 'sm12',
  'hidden fates': 'sm115',
  'hidden fates shiny vault': 'sma',
  'shiny vault': 'sma',
  'shining legends': 'sm35',

  // Sword & Shield Series
  'sword shield': 'swsh1',
  'sword & shield': 'swsh1',
  'sword and shield': 'swsh1',
  'rebel clash': 'swsh2',
  'darkness ablaze': 'swsh3',
  'vivid voltage': 'swsh4',
  'battle styles': 'swsh5',
  'chilling reign': 'swsh6',
  'evolving skies': 'swsh7',
  'fusion strike': 'swsh8',
  'brilliant stars': 'swsh9',
  'brilliant stars trainer gallery': 'swsh9tg',
  'astral radiance': 'swsh10',
  'astral radiance trainer gallery': 'swsh10tg',
  'lost origin': 'swsh11',
  'lost origin trainer gallery': 'swsh11tg',
  'silver tempest': 'swsh12',
  'silver tempest trainer gallery': 'swsh12tg',
  'crown zenith': 'swsh12pt5',
  'crown zenith galarian gallery': 'swsh12pt5gg',
  'champions path': 'swsh35',
  'shining fates': 'swsh45',
  'shining fates shiny vault': 'swsh45sv',

  // Scarlet & Violet Series
  'surging sparks': 'sv8',
  'prismatic evolutions': 'sv8pt5',
  'scarlet & violet': 'sv1',
  'scarlet and violet': 'sv1',
  'scarlet violet': 'sv1',
  'temporal forces': 'sv5',
  'paldean fates': 'sv4pt5',
  'twilight masquerade': 'sv6',
  'shrouded fable': 'sv6pt5',
  'stellar crown': 'sv7',
  'journey together': 'sv9',
  'scarlet & violet black star promos': 'svp',
  'scarlet and violet black star promos': 'svp',
  'scarlet violet black star promos': 'svp',
  'scarlet & violet energies': 'sve',
  'scarlet and violet energies': 'sve',
  'scarlet violet energies': 'sve',
  'paldea evolved': 'sv2',
  'obsidian flames': 'sv3',
  '151': 'sv3pt5',
  'paradox rift': 'sv4',

  // Promos
  'wizards black star promos': 'basep',
  'nintendo black star promos': 'np',
  'nintendo promos': 'np',
  'black star promos': 'np',
  'dp black star promos': 'dpp',
  'hgss black star promos': 'hsp',
  'bw black star promos': 'bwp',
  'xy black star promos': 'xyp',
  'sm black star promos': 'smp',
  'swsh black star promos': 'swshp',

  // Other Sets
  'pokemon futsal collection': 'fut20',
  'pokemon futsal': 'fut20',
  'pokemon go': 'pgo',
  'pokémon go': 'pgo',
  'pokemon rumble': 'ru1',
  'rumble': 'ru1',
  'detective pikachu': 'det1',
  'detective pikachu series': 'det1',
  'celebrations classic collection': 'cel25c',
  'celebrations: classic collection': 'cel25c',
  'classic collection': 'cel25c',
  'celebrations': 'cel25',
  'southern islands': 'si1',
  'best of game': 'bp',

  // McDonald's Collections
  'mcdonalds collection 2011': 'mcd11',
  'mcdonalds collection 2012': 'mcd12',
  'mcdonalds collection 2013': 'mcd13',
  'mcdonalds collection 2014': 'mcd14',
  'mcdonalds collection 2015': 'mcd15',
  'mcdonalds collection 2016': 'mcd16',
  'mcdonalds collection 2017': 'mcd17',
  'mcdonalds collection 2018': 'mcd18',
  'mcdonalds collection 2019': 'mcd19',
  'mcdonalds collection 2021': 'mcd21',
  'mcdonalds collection 2022': 'mcd22',
  'mcdonalds collection 2023': 'mcd23',
  'mcdonalds collection': null,
  'mcdonalds promo': null,
  'mcdonalds promos': null,

  // Trainer Kits
  'ex trainer kit latias': 'tk1a',
  'ex trainer kit latios': 'tk1b',
  'ex trainer kit 2 plusle': 'tk2a',
  'ex trainer kit 2 minun': 'tk2b'
};

// Function to normalize set names to their IDs
function normalizeSetName(setName: string): string | null {
  const normalized = setName.toLowerCase().trim();
  console.log('Normalizing set name:', setName, 'to:', normalized);
  
  // Try exact match first
  const exactMatch = SET_NAME_MAPPING[normalized];
  if (exactMatch) {
    console.log('Found exact match:', exactMatch);
    return exactMatch;
  }

  // Try with spaces normalized
  const normalizedSpaces = normalized.replace(/\s+/g, ' ');
  const spaceMatch = SET_NAME_MAPPING[normalizedSpaces];
  if (spaceMatch) {
    console.log('Found match with normalized spaces:', spaceMatch);
    return spaceMatch;
  }

  // Try with '&' replaced with 'and'
  const andMatch = SET_NAME_MAPPING[normalizedSpaces.replace(/ & /g, ' and ')];
  if (andMatch) {
    console.log('Found match with "and":', andMatch);
    return andMatch;
  }

  console.log('No mapping found for:', normalized);
  return SET_NAME_MAPPING[normalized] || null;
}

async function getAllSets(): Promise<PokemonSet[]> {
  try {
    // Check memory cache first
    const cachedSets = getFromCache<PokemonSet[]>('sets', 'all');
    if (cachedSets) {
      return cachedSets;
    }

    const { data, error } = await supabase
      .from('pokemontcg_io_sets')
      .select('*')
      .order('release_date', { ascending: false });
    
    if (error) {
      console.error('Error fetching sets:', error);
      return [];
    }
    
    // Transform data to match PokemonSet interface
    const sortedSets = data.map(set => ({
      ...set,
      releaseDate: set.release_date,
      images: {
        symbol: set.symbol_url || null,
        logo: set.logo_url || null
      }
    })).sort((a: PokemonSet, b: PokemonSet) =>
      new Date(b.release_date).getTime() - new Date(a.release_date).getTime()
    );
    
    // Cache the results
    setInCache('sets', 'all', sortedSets);
    
    return sortedSets;
  } catch (error) {
    console.error('Error fetching sets:', error);
    throw new Error('Failed to fetch sets. Please try again later.');
  }
}

async function searchAllCards(query: string): Promise<Card[]> {
  try {
    if (!query?.trim()) return [];

    // Check if searching for McDonald's cards by year
    const mcdonaldsMatch = query.toLowerCase().match(/mcdonalds.*?(20\d{2}|collection)/);
    if (mcdonaldsMatch) {
      const year = mcdonaldsMatch[1];
      const yearSuffix = year.match(/20\d{2}/) ? year.slice(-2) : '';
      const setId = yearSuffix ? `mcd${yearSuffix}` : null;

      console.log('Searching for McDonald\'s cards');
      let dbQuery = supabase
        .from('pokemontcg_io')
        .select(`
          *,
          set:pokemontcg_io_sets(*)
        `);

      // If year specified, filter by specific set ID
      if (setId) {
        console.log('Filtering by McDonald\'s set:', setId);
        dbQuery = dbQuery.eq('set_id', setId);
      } else {
        // Otherwise show all McDonald's cards
        console.log('Showing all McDonald\'s cards');
        dbQuery = dbQuery.ilike('id', 'mcd%');
      }

      const { data, error } = await dbQuery
        .order('release_date', { ascending: false });

      if (error) {
        console.error('Error searching McDonald\'s cards:', error);
        return [];
      }

      if (!data) {
        console.log(`No McDonald's cards found${setId ? ` for ${year}` : ''}`);
        return [];
      }

      console.log(`Found ${data.length} McDonald's cards${setId ? ` for ${year}` : ''}`);
      return data.map(card => ({
        ...card,
        flavor: card.flavor_text,
        images: {
          small: card.image_small_url,
          large: card.image_large_url
        },
        set: {
          ...card.set,
          releaseDate: card.set.release_date,
          images: {
            symbol: card.set.symbol_url,
            logo: card.set.logo_url
          }
        }
      }));
    }

    // Extract search terms
    const terms = query.match(/([^:]+):("[^"]+"|[^\s]+)/g) || [];
    let searchQuery = query;

    // Build the database query
    let dbQuery = supabase.from('pokemontcg_io').select(`
      id,
      name,
      number,
      set_id,
      set_name,
      series,
      set_total,
      release_date,
      market_price_usd,
      market_price_low_usd,
      market_price_trend_usd,
      market_price_reverse_holo_usd,
      hp,
      types,
      evolves_from,
      evolves_to,
      abilities,
      attacks,
      weaknesses,
      retreat_cost,
      rarity,
      image_small_url,
      image_large_url,
      flavor_text
    `);

    // Handle special search terms
    terms.forEach(term => {
      const [field, value] = term.split(':');
      const cleanValue = value.replace(/"/g, '').trim();

      switch (field.trim()) {
        case 'name':
          dbQuery = dbQuery.ilike('name', `%${cleanValue}%`);
          break;
        case 'set.name':
          dbQuery = dbQuery.ilike('set_name', `%${cleanValue}%`);
          break;
        case 'number':
          dbQuery = dbQuery.eq('number', cleanValue);
          break;
      }
      // Remove the processed term from the general search
      searchQuery = searchQuery.replace(term, '').trim();
    });

    // Add general search if no specific terms
    if (!terms.length && searchQuery) {
      dbQuery = dbQuery.ilike('name', `%${searchQuery}%`);
    }
    
    // Execute query with limit
    const { data, error } = await dbQuery.limit(1000);
    
    if (error) {
      console.error('Error searching cards:', error);
      return [];
    }

    if (!data) {
      console.log('No cards found for query:', query);
      return [];
    }
    
    // Validate card data
    const validCards = data.filter((card: any) => (
      card?.id &&
      card?.name &&
      card?.set_id &&
      card?.set_name &&
      card?.number
    ));

    console.log(`Found ${validCards.length} valid cards`);
    return validCards.map(card => ({
      ...card,
      flavor: card.flavor_text,
      images: {
        small: card.image_small_url,
        large: card.image_large_url
      },
      set: {
        id: card.set_id,
        name: card.set_name,
        series: card.series,
        total: card.set_total,
        releaseDate: card.release_date
      }
    }));
  } catch (error) {
    console.error('Error searching cards:', error);
    throw new Error('Failed to search cards. Please try again later.');
  }
}

async function getSetCards(setId: string, isRevalidation = false): Promise<Card[]> {
  try {
    if (!setId) {
      console.error('Invalid setId provided to getSetCards');
      return [];
    }

    // Check memory cache first
    if (!isRevalidation) {
      const cachedCards = getFromCache<Card[]>('set', setId);
      if (cachedCards) {
        console.log(`Returning ${cachedCards.length} cached cards for set ${setId}`);
        return cachedCards;
      }
    }

    console.log('Fetching cards for set:', setId);

    // First try to get all cards at once
    const { data: allCards, error } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*)
      `)
      .eq('set_id', setId)
      .order('number');
    
    if (error) {
      console.error('Error fetching cards:', error);
      throw error;
    }

    if (!allCards || allCards.length === 0) {
      console.log('No cards found for set:', setId);
      return [];
    }

    console.log(`Found ${allCards.length} cards for set ${setId}`);

    // Transform and cache the cards
    const transformedCards = allCards.map(card => ({
      ...card,
      flavor: card.flavor_text,
      images: {
        small: card.image_small_url,
        large: card.image_large_url
      },
      set: {
        ...card.set,
        releaseDate: card.set.release_date,
        images: {
          symbol: card.set.symbol_url,
          logo: card.set.logo_url
        }
      }
    }));
    
    // Cache the results
    if (!isRevalidation) {
      console.log(`Caching ${transformedCards.length} cards for set ${setId}`);
      setInCache('set', setId, transformedCards);
    }
    
    return transformedCards;
  } catch (error) {
    console.error('Error fetching set cards:', error instanceof Error ? error.message : error);
    return []; // Return empty array instead of throwing to prevent infinite loading
  }
}

async function getCard(cardId: string): Promise<Card | null> {
  try {
    if (!cardId) {
      throw new Error('No cardId provided');
    }

    // First get the main card data
    const { data, error } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*),
        evolves_from_id,
        evolves_to_id
      `)
      .eq('id', cardId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching card:', error);
      return null;
    }

    if (data) {
      // Get evolution chain IDs if not already present
      if (data.evolves_from) {
        const { data: preEvo } = await supabase
          .from('pokemontcg_io')
          .select('id, name')
          .eq('name', data.evolves_from)
          .eq('set_id', data.set_id)
          .maybeSingle();
        
        if (preEvo) {
          data.evolves_from_id = preEvo.id;
          data.evolves_from = preEvo.name;
        }
      }

      if (data.evolves_to && data.evolves_to.length > 0) {
        const { data: evo } = await supabase
          .from('pokemontcg_io')
          .select('id, name')
          .eq('name', data.evolves_to[0])
          .eq('set_id', data.set_id)
          .maybeSingle();
        
        if (evo) {
          data.evolves_to_id = [evo.id];
          data.evolves_to = [evo.name];
        }
      }
    }
    return data ? {
      ...data,
      flavor: data.flavor_text,
      images: {
        small: data.image_small_url,
        large: data.image_large_url
      },
      set: {
        ...data.set,
        releaseDate: data.set.release_date,
        images: {
          symbol: data.set.symbol_url,
          logo: data.set.logo_url
        }
      }
    } : null;
  } catch (error) {
    console.error('Error fetching card:', error);
    throw new Error('Failed to fetch card details. Please try again later.');
  }
}

async function getSimilarCards(card: Card): Promise<Card[]> {
  try {
    if (!card?.name || !card?.id) {
      console.log('Invalid card data for similar cards:', card);
      return [];
    }
    
    // Get base name without any suffixes (V, VMAX, etc)
    const baseName = card.name.split(' ')[0];
    
    const { data, error } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*)
      `)
      .ilike('name', `${baseName}%`)
      .neq('id', card.id)
      .order('release_date', { ascending: false })
      .limit(20);
    
    if (error) {
      console.error('Error fetching similar cards:', error);
      return [];
    }

    if (!data) {
      console.log('No similar cards found');
      return [];
    }
    
    
    
    const similarCards = data.map(card => ({
      ...card,
      flavor: card.flavor_text,
      images: {
        small: card.image_small_url,
        large: card.image_large_url
      },
      set: {
        ...card.set,
        releaseDate: card.set.release_date,
        images: {
          symbol: card.set.symbol_url,
          logo: card.set.logo_url
        }
      }
    }));

    console.log(`Found ${similarCards.length} similar cards`);
    return similarCards;
  } catch (error) {
    console.error('Error fetching similar cards:', error);
    return [];
  }
}

async function getMostValuableCards(limit: number = 10): Promise<Card[]> {
  try {
    // Fetch cards sorted by market_price_trend_usd
    const { data, error } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*)
      `)
      .not('market_price_trend_usd', 'is', null)
      .order('market_price_trend_usd', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Error fetching valuable cards:', error);
      return valuableCards.slice(0, limit);
    }
    
    if (!data) {
      console.log('No valuable cards found, using static data');
      return valuableCards.slice(0, limit);
    }
    
    // Validate and filter cards
    const validCards = data.filter((card: any) => (
      card?.id &&
      card?.name &&
      card?.set?.id &&
      card?.set?.id &&
      card?.number &&
      card.market_price_trend_usd > 0
    ));

    console.log(`Found ${validCards.length} valid valuable cards`);
    
    // Transform data to match Card interface
    return validCards.map(card => ({
      ...card,
      flavor: card.flavor_text,
      images: {
        small: card.image_small_url,
        large: card.image_large_url
      },
      set: {
        ...card.set,
        releaseDate: card.set.release_date,
        images: {
          symbol: card.set.symbol_url,
          logo: card.set.logo_url
        }
      },
      cardmarket: {
        prices: {
          averageSellPrice: card.market_price_trend_usd,
          lowPrice: card.market_price_low_usd,
          trendPrice: card.market_price_trend_usd,
          reverseHoloTrend: card.market_price_reverse_holo_usd || 0
        }
      }
    })).slice(0, limit);
  } catch (error) {
    console.error('Error fetching valuable cards:', error instanceof Error ? error.message : error);
    // Fallback to static data if API fails
    console.log('Falling back to static valuable cards data');
    return valuableCards.slice(0, limit);
  }
}

async function getFoilCardsFromCollection(foilItems: any[]): Promise<Card[]> {
  try {
    const cardPromises = foilItems.map(item => 
      fetch(`https://api.pokemontcg.io/v2/cards/${item.cardId}`, {
        headers: {
          'X-Api-Key': '51f8f8de-7ac2-4d08-8172-9d3e2c7ac48d'
        }
      }).then(res => res.json())
    );
    
    const cardResponses = await Promise.all(cardPromises);
    const validCards = cardResponses
      .filter(response => response.data)
      .map(response => response.data);
    
    // Map collection quantities to cards
    const cardsWithQuantities = validCards.flatMap(card =>
      Array.from({ length: foilItems.find(item => item.cardId === card.id)?.quantity || 0 })
        .map((_, index) => ({
          ...card,
          uniqueId: `${card.id}-${Date.now()}-${index}`,
          images: {
            small: card.image_small_url,
            large: card.image_large_url
          }
        }))
    );

    return cardsWithQuantities;
  } catch (error) {
    console.error('Error fetching cards:', error);
    return [];
  }
}

// Single export statement for all functions
export {
  getAllSets,
  searchAllCards,
  getSetCards,
  getCard,
  getSimilarCards,
  getMostValuableCards,
  normalizeSetName,
  getFoilCardsFromCollection
};